/**
 * Single source of truth für die rechtlich zwingenden Betreiber-Angaben.
 *
 * Alle Felder mit `PLACEHOLDER_` am Anfang MÜSSEN vor Livegang durch echte
 * Daten ersetzt werden — sonst ist das Impressum nach § 5 DDG/TMG nicht
 * vollständig und das Angebot abmahnbar. Die Impressum-Seite rendert eine
 * fette Warnung, wenn sie PLACEHOLDER_-Strings entdeckt, damit niemand
 * versehentlich mit Dummy-Daten produktiv geht.
 *
 * Rechtsrahmen (Stand April 2026):
 *   • DDG (Digitale-Dienste-Gesetz, ersetzte TMG am 14.05.2024) § 5
 *   • RStV § 55 Abs. 2 (journalistisch-redaktionelle Angebote)
 *   • DSGVO Art. 13 (Informationspflichten bei Erhebung)
 *   • EU DSA Art. 26/27 (Transparenzanforderungen — greifen erst ab
 *     Schwellwerten, hier nicht einschlägig).
 */

export interface LegalConfig {
  /** Juristischer Name inkl. Rechtsform, z.B. "ACME Legal-Tech GmbH". */
  companyName: string;
  /** Verantwortliche Person i.S.d. § 18 Abs. 2 MStV. */
  responsiblePerson: string;
  /** Vertretungsberechtigte Geschäftsführer (bei GmbH/UG/AG), Komma-getrennt. */
  managingDirectors: string;
  /** Straße und Hausnummer. */
  streetAddress: string;
  /** PLZ und Ort. */
  postalCodeCity: string;
  /** Land — wir geben DE voraus, sonst anpassen. */
  country: string;
  /** Kontakt-Email für Impressum + Datenschutz + Support. */
  contactEmail: string;
  /** Optional: Telefon. Kann leer bleiben, wenn die Email-Antwortzeit reicht. */
  phone?: string;
  /** USt-ID nach § 27a UStG, z.B. "DE123456789". Optional, wenn Kleinunternehmer. */
  vatId?: string;
  /** Handelsregistergericht, z.B. "Amtsgericht Düsseldorf". */
  registryCourt?: string;
  /** HRB/HRA-Nummer, z.B. "HRB 987654". */
  registryNumber?: string;
  /**
   * DPO (Datenschutzbeauftragter). Für die meisten kleinen SaaS NICHT
   * verpflichtend (< 20 Mitarbeiter in automatisierter Verarbeitung), aber
   * sinnvoll anzugeben, wenn vorhanden.
   */
  dpo?: { name: string; email: string };
  /**
   * Eine natürliche Person, die im Produkt selbst erkennbar ist —
   * z.B. bei Solo-Gründer derselbe wie `responsiblePerson`. Wird für
   * RStV § 55 Abs. 2 verwendet.
   */
  journalisticResponsible?: string;
}

/**
 * Produktive Werte.
 *
 * ⚠️  Diese sind PLATZHALTER und müssen vor dem Livegang ersetzt werden.
 *     Die Impressum-Komponente prüft zur Laufzeit, ob noch PLACEHOLDER_-
 *     Strings auftauchen, und rendert einen Warnhinweis. Das verhindert,
 *     dass das Dashboard mit Dummy-Impressum in den Umlauf kommt.
 */
export const LEGAL: LegalConfig = {
  companyName: "PLACEHOLDER_FIRMA_INKL_RECHTSFORM",
  responsiblePerson: "PLACEHOLDER_VOR_NACHNAME",
  managingDirectors: "PLACEHOLDER_GESCHAEFTSFUEHRER",
  streetAddress: "PLACEHOLDER_STRASSE_HAUSNR",
  postalCodeCity: "PLACEHOLDER_PLZ_ORT",
  country: "Deutschland",
  contactEmail: "info@yavu.de",
  phone: undefined,
  vatId: undefined,
  registryCourt: undefined,
  registryNumber: undefined,
  dpo: undefined,
  journalisticResponsible: "PLACEHOLDER_VOR_NACHNAME",
};

/** Helfer: ist ein Wert noch ein Platzhalter? */
export function isPlaceholder(value: string | undefined | null): boolean {
  return typeof value === "string" && value.startsWith("PLACEHOLDER_");
}

/** Gibt alle Platzhalter-Felder als String-Liste zurück (für Warnhinweis). */
export function listPlaceholders(cfg: LegalConfig = LEGAL): string[] {
  const out: string[] = [];
  for (const [k, v] of Object.entries(cfg)) {
    if (typeof v === "string" && isPlaceholder(v)) out.push(k);
  }
  return out;
}

/** Produkt-Metadaten für Datenschutz + AGB. */
export const PRODUCT = {
  name: "Shadow Court",
  domain: "shadow-court.pages.dev",
  tagline: "Mock-Jury & Urteilsprognose — keine Rechtsberatung.",
  /**
   * Lebzeichen: Wir listen öffentlich, welche Auftragsverarbeiter wir
   * einsetzen. Das ist DSGVO-Pflicht (Art. 13 Abs. 1 lit. e + f) und
   * stärkt Vertrauen.
   */
  subProcessors: [
    { name: "Cloudflare, Inc.", purpose: "Hosting, CDN, Workers-Runtime, R2-Speicher, D1-Datenbank, Vectorize-Index", location: "USA/EU (CF-Rechenzentrum EU-Routing aktiv)" },
    { name: "OpenAI, L.L.C.",   purpose: "LLM-Inference für den Jury-Schwarm (optional)", location: "USA (EU-SCC Module 2)" },
    { name: "DeepSeek (Hangzhou) Ltd.", purpose: "LLM-Inference für den Jury-Schwarm", location: "China / Gateway via Cloudflare AI Gateway" },
    { name: "Stripe Payments Europe, Ltd.", purpose: "Zahlungsabwicklung", location: "Irland / EU" },
    { name: "Resend (if enabled)", purpose: "Transaktionale E-Mails (Magic-Link-Login)", location: "USA (EU-SCC)" },
  ],
};
